import { ColumnMetadata } from "../../../shared/smartColumnApiService";


const sanitizeValueForExcel = (value: any): any => {

  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "number") {
    if (isNaN(value) || !isFinite(value)) {
      return ""; 
    }
    return value;
  }

  if (typeof value === "string" || typeof value === "boolean") {
    return value;
  }

  if (typeof value === "object") {
    if (Array.isArray(value)) {
      for (const item of value) {
        const sanitized = sanitizeValueForExcel(item);
        if (sanitized !== "") {
          return sanitized;
        }
      }
      return "";
    }
    return String(value);
  }

  return String(value);
};

export const createNewSheetWithResults = async (
  results: Array<{ [key: string]: any }>,
  returnedColumns: string[],
  columnMetadata: ColumnMetadata[],
  sourceSheetName?: string
): Promise<boolean> => {
  let newSheetName: string = "";

  try {
    await Excel.run(async (context) => {
      newSheetName = await generateUniqueSheetName(context, sourceSheetName);

      console.log(`📝 Creating sheet: ${newSheetName}`);
      const newSheet = context.workbook.worksheets.add(newSheetName);

      if (!results || results.length === 0) {
        console.warn("⚠️ No results to populate");
        await context.sync();
        return;
      }

      if (!returnedColumns || returnedColumns.length === 0) {
        console.error("❌ No columns provided");
        throw new Error("No columns provided in response");
      }

      console.log(
        `📊 Populating sheet with ${results.length} rows × ${returnedColumns.length} columns`
      );
      console.log(`📊 All columns:`, returnedColumns);

      const metadataMap = new Map<string, ColumnMetadata>();
      columnMetadata.forEach((meta) => {
        metadataMap.set(meta.column_name, meta);
      });

      const allHeaders = returnedColumns;
      const rowCount = results.length;
      const lastRowNum = rowCount + 1;
      const lastColumn = getColumnLetter(allHeaders.length - 1);

      const headerRange = newSheet.getRange(`A1:${lastColumn}1`);
      headerRange.values = [allHeaders];
      await context.sync();

      const dataRows: any[][] = [];
      const columnDataCheck = new Map<string, { hasData: boolean; sampleValue: any }>();

      for (const row of results) {
        const rowData: any[] = [];
        for (const header of allHeaders) {
          const value = row[header];
          
          const sanitizedValue = sanitizeValueForExcel(value);

          if (
            !columnDataCheck.has(header) &&
            sanitizedValue !== null &&
            sanitizedValue !== undefined &&
            sanitizedValue !== ""
          ) {
            columnDataCheck.set(header, { hasData: true, sampleValue: sanitizedValue });
          }

          rowData.push(sanitizedValue);
        }
        dataRows.push(rowData);
      }

      console.log(`🔍 Column data check:`);
      allHeaders.forEach((header) => {
        const check = columnDataCheck.get(header);
        if (check) {
          console.log(`   ✅ ${header}: has data (sample: ${check.sampleValue})`);
        } else {
          console.log(`   ⚠️ ${header}: NO DATA (all empty/null)`);
        }
      });

      const dataRange = newSheet.getRange(`A2:${lastColumn}${lastRowNum}`);
      dataRange.values = dataRows;
      await context.sync();

      console.log(`✅ Inserted ${rowCount} data rows with pre-computed values from API`);

      const tableRange = newSheet.getRange(`A1:${lastColumn}${lastRowNum}`);
      const table = newSheet.tables.add(tableRange, true);
      table.name = await generateUniqueTableName(context, newSheetName);
      table.style = "TableStyleMedium2";

      await context.sync();
      console.log(`✅ Excel Table created: ${table.name}`);

      console.log(
        `ℹ️  Formula columns use pre-computed values from API (no Excel formulas needed)`
      );

      for (const metadata of columnMetadata) {
        if (metadata.formula && metadata.column_type === "formula") {
          console.log(`📝 Formula column '${metadata.column_name}' uses pre-computed values`);
          console.log(`   Formula (for reference): ${metadata.formula}`);
        }
      }

      const usedRange = newSheet.getUsedRange();
      if (usedRange) {
        usedRange.format.autofitColumns();
      }

      const firstCell = newSheet.getRange("A1");
      firstCell.select();

      newSheet.activate();
      await context.sync();

      console.log(
        `✅ Successfully populated ${results.length} rows × ${returnedColumns.length} columns`
      );
    });

    console.log(
      `🎉 SUCCESS: New sheet '${newSheetName}' created with ${returnedColumns.length} column(s) and ${results.length} row(s)`
    );
    return true;
  } catch (error) {
    console.error("❌ Error creating sheet with results:", error);
    throw error;
  }
};

export const getColumnLetter = (index: number): string => {
  let letter = "";
  let num = index;
  while (num >= 0) {
    letter = String.fromCharCode(65 + (num % 26)) + letter;
    num = Math.floor(num / 26) - 1;
  }
  return letter;
};

const sanitizeTableName = (name: string): string => {
  return name
    .replace(/[^A-Za-z0-9_.]/g, "_")
    .replace(/_{2,}/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^(\d)/, "_$1");
};

const generateUniqueTableName = async (
  context: Excel.RequestContext,
  sheetName: string
): Promise<string> => {
  const baseName = sanitizeTableName(`Table_${sheetName}`);

  const tables = context.workbook.tables;
  tables.load("items/name");
  await context.sync();

  const existingNames = new Set(tables.items.map((t) => t.name));

  if (!existingNames.has(baseName)) {
    return baseName;
  }

  let counter = 2;
  let newName = `${baseName}_${String(counter).padStart(2, "0")}`;

  const MAX_ATTEMPTS = 1000;
  while (existingNames.has(newName) && counter < MAX_ATTEMPTS) {
    counter++;
    newName = `${baseName}_${String(counter).padStart(2, "0")}`;
  }

  if (existingNames.has(newName)) {
    throw new Error(`Unable to generate a unique table name after ${MAX_ATTEMPTS} attempts.`);
  }

  return newName;
};

export const generateUniqueSheetName = async (
  context: Excel.RequestContext,
  sourceSheetName?: string
): Promise<string> => {
  const baseName = sourceSheetName ? `SC_${sourceSheetName.substring(0, 25)}` : "SC_SmartColumn";

  const worksheets = context.workbook.worksheets;
  worksheets.load("items/name");
  await context.sync();

  const existingNames = worksheets.items.map((ws) => ws.name);

  if (!existingNames.includes(baseName)) {
    return baseName;
  }

  let counter = 2;
  let newName = `${baseName}_${String(counter).padStart(2, "0")}`;

  const MAX_ATTEMPTS = 1000;
  while (existingNames.includes(newName) && counter < MAX_ATTEMPTS) {
    counter++;
    newName = `${baseName}_${String(counter).padStart(2, "0")}`;
  }

  if (existingNames.includes(newName)) {
    throw new Error(`Unable to generate a unique sheet name after ${MAX_ATTEMPTS} attempts.`);
  }

  return newName;
};